import type { SupabaseClient } from '../../lib/supabase';
import { DRIVE_ERROR_MESSAGES } from './drive';
import type {
  FileBrowserFilters,
  FileBrowserItem,
  FileCategory,
  FileSource,
  FileWithUploader,
} from './types';

/**
 * Database access for file metadata. Google Drive holds the binaries; these
 * rows are what the app renders and what Row Level Security governs. Every
 * permission rule (who sees, attaches, edits, approves, deletes) is decided
 * by the database and proved by the Phase 4 Stage 1 staging assertions.
 */

function fail(message: string): never {
  throw new Error(message);
}

interface FileRow {
  id: string;
  task_id: string | null;
  project_id: string;
  source: FileSource;
  drive_file_id: string | null;
  url: string;
  name: string;
  mime_type: string | null;
  size_bytes: number | null;
  category: FileCategory;
  uploaded_by: string;
  review_status: FileWithUploader['review_status'];
  is_supplier_visible: boolean;
  asset_group_id: string | null;
  version_number: number;
  is_latest_version: boolean;
  version_note: string | null;
  superseded_at: string | null;
  created_at: string;
  profiles: { display_name: string; email: string } | null;
}

// One literal, deliberately unwrapped: PostgREST infers the row type from
// the exact string, and concatenating it collapses that inference to unknown.
// prettier-ignore
const FILE_COLUMNS = 'id, task_id, project_id, source, drive_file_id, url, name, mime_type, size_bytes, category, uploaded_by, review_status, is_supplier_visible, asset_group_id, version_number, is_latest_version, version_note, superseded_at, created_at, profiles:uploaded_by (display_name, email)';

/**
 * One task's files, newest first, joined to each uploader. Superseded
 * versions are included: the interface separates latest from history.
 */
export async function fetchTaskFiles(
  client: SupabaseClient,
  taskId: string,
  projectId = '',
): Promise<FileWithUploader[]> {
  // An empty taskId means the project's own attachments (task_id null).
  const base = client.from('task_files').select(FILE_COLUMNS);
  const scoped =
    taskId === ''
      ? base.is('task_id', null).eq('project_id', projectId)
      : base.eq('task_id', taskId);
  const { data, error } = await scoped.order('created_at', { ascending: false });

  if (error) {
    fail(error.message);
  }

  return ((data ?? []) as unknown as FileRow[]).map((row) => {
    const { profiles, ...file } = row;
    return {
      ...file,
      uploader_name: profiles
        ? profiles.display_name.trim() !== ''
          ? profiles.display_name
          : profiles.email
        : '',
    };
  });
}

/**
 * Attachment counts per task for the List View indicator: one read. Counts
 * logical assets, not versions — superseded rows would otherwise inflate a
 * task's badge every time a file is revised.
 */
export async function fetchFileCounts(
  client: SupabaseClient,
  projectId: string,
): Promise<Record<string, number>> {
  const { data, error } = await client
    .from('task_files')
    .select('task_id')
    .eq('project_id', projectId)
    .eq('is_latest_version', true);

  if (error) {
    fail(error.message);
  }
  const counts: Record<string, number> = {};
  for (const row of data ?? []) {
    if (row.task_id !== null) {
      counts[row.task_id] = (counts[row.task_id] ?? 0) + 1;
    }
  }
  return counts;
}

export const FILE_BROWSER_PAGE_SIZE = 50;

/** User input inside an ilike pattern must not act as wildcards. */
function escapeSearchTerm(term: string): string {
  return term.replace(/[\\%_]/g, (character) => `\\${character}`);
}

/**
 * One page of the cross-project Files browser: latest versions of every
 * file the caller may see, newest first, filtered server-side (handoff 39:
 * paginate, never fetch the world). Row Level Security is the only
 * permission decision.
 */
export async function fetchFileBrowserPage(
  client: SupabaseClient,
  filters: FileBrowserFilters,
  offset: number,
): Promise<FileBrowserItem[]> {
  // Through the gated function rather than the table directly. The browser is
  // the one surface the files permission governs; attachments inside a project
  // or a task read task_files as they always have, because losing the file on
  // your own task is not what "no access to Files" means.
  const term = filters.search.trim();
  // The three filter arguments are nullable in SQL — file_browser_page is not
  // STRICT, and null is how "no filter" is expressed. The generated types call
  // them plain strings because Postgres cannot describe argument nullability,
  // so the canonical generator has nothing to read. Narrowed here rather than
  // by hand-editing the generated file: editing that file is what let it drift
  // out of sync with the schema in the first place.
  const args = {
    p_search: term === '' ? null : escapeSearchTerm(term),
    p_category: filters.category === 'all' ? null : filters.category,
    p_project_id: filters.projectId === 'all' ? null : filters.projectId,
    p_limit: FILE_BROWSER_PAGE_SIZE,
    p_offset: offset,
  } as unknown as Parameters<typeof client.rpc<'file_browser_page'>>[1];

  const { data, error } = await client.rpc('file_browser_page', args);

  if (error) {
    fail(error.message);
  }

  return (data ?? []) as unknown as FileBrowserItem[];
}

export interface InsertFileRecord {
  taskId: string;
  projectId: string;
  source: FileSource;
  driveFileId: string | null;
  url: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
  category: FileCategory;
  uploadedBy: string;
  assetGroupId?: string;
  versionNote?: string;
}

/**
 * Records one attachment. The version chain columns are deliberately never
 * sent: the database computes them (task_files_place_version).
 */
export async function insertFileRecord(
  client: SupabaseClient,
  input: InsertFileRecord,
): Promise<void> {
  const { error } = await client.from('task_files').insert({
    // Empty task means the row belongs to the project itself.
    task_id: input.taskId === '' ? null : input.taskId,
    project_id: input.projectId,
    source: input.source,
    drive_file_id: input.driveFileId,
    url: input.url,
    name: input.name.trim(),
    mime_type: input.mimeType,
    size_bytes: input.sizeBytes,
    category: input.category,
    uploaded_by: input.uploadedBy,
    asset_group_id: input.assetGroupId ?? null,
    version_note: input.versionNote ?? null,
  });

  if (error) {
    fail(error.message);
  }
}

/** The columns the database grants for update, nothing more. */
export interface FileUpdate {
  name?: string;
  category?: FileCategory;
  review_status?: FileWithUploader['review_status'];
  version_note?: string | null;
  /** Sharing into the supplier review branch (handoff 29/33, Phase 6). */
  is_supplier_visible?: boolean;
}

export async function updateFile(
  client: SupabaseClient,
  fileId: string,
  changes: FileUpdate,
): Promise<void> {
  const { data, error } = await client
    .from('task_files')
    .update(changes)
    .eq('id', fileId)
    .select('id');

  if (error) {
    fail(error.message);
  }
  if ((data ?? []).length === 0) {
    fail('file_edit_not_permitted');
  }
}

export async function deleteFile(client: SupabaseClient, fileId: string): Promise<void> {
  const { data, error } = await client.from('task_files').delete().eq('id', fileId).select('id');

  if (error) {
    fail(error.message);
  }
  // Policy-filtered deletions come back as zero rows; say so rather than
  // pretending the file went away.
  if ((data ?? []).length === 0) {
    fail('file_delete_not_permitted');
  }
}

/**
 * Database refusals worth explaining. Trigger wordings are stable; the
 * Drive-side messages are shared from the Drive client.
 */
export const FILE_ERROR_MESSAGES: Record<string, string> = {
  ...DRIVE_ERROR_MESSAGES,
  file_project_must_match_task: 'This file belongs to a different project.',
  file_identity_is_immutable: 'A file cannot be moved or reattributed.',
  file_version_chain_is_database_owned: 'Version history cannot be edited by hand.',
  review_status_requires_manager: 'Only the project owner or an admin can change review status.',
  approved_files_require_manager:
    'Approved files can only be removed by the project owner or an admin.',
  version_must_stay_on_its_task: 'A new version must be uploaded on the same task.',
  file_edit_not_permitted: 'You cannot edit this file.',
  file_delete_not_permitted:
    'You cannot delete this file. Approved files need the project owner or an admin.',
};

/** Turns a database or Drive error message into something a person can act on. */
export function fileErrorMessage(message: string): string {
  return FILE_ERROR_MESSAGES[message] ?? 'That file change could not be saved. Try again.';
}
